import { createEffect, createSignal, For, Show } from 'solid-js'
import { Title, Meta, Link } from '@solidjs/meta'
import { PageHero } from '/web/shared/aifans/PageHero'
import Footer from '/web/shared/aifans/Footer'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import Fuse from 'fuse.js'

type FAQItem = {
  category: string
  heading: string
  para: string
}

const faqData: FAQItem[] = [
  // Getting Started
  {
    category: 'Getting Started',
    heading: 'What is CosplayFans?',
    para: `At CosplayFans, transform your fantasies into reality and connect with your favorite creators. You can view free images our creators share, stay updated by following your favorite creators, subscribe to their personal page for exclusive content, purchase tokens and use them for personal content generation, express appreciation by sending tips to creators, and engage with content through likes and comments on the platform.`,
  },
  {
    category: 'Getting Started',
    heading: 'What type of content can I find on CosplayFans?',
    para: `On CosplayFans, you can discover a wide variety of adult entertainment content, including live chats, exclusive creator content, and full control over the content of your favorite character. The platform offers interactive experiences with tons of characters, personalized content from characters, and a wide variety of genres, catering to a diverse audience seeking engaging adult entertainment.`,
  },
  // Tokens & Payments
  {
    category: 'Tokens & Payments',
    heading: 'What are tokens?',
    para: `Tokens serve as a form of online currency that you use across CosplayFans to make purchases on the site.`,
  },
  {
    category: 'Tokens & Payments',
    heading:
      "I'm worried about what my bank will think. How does the payment look on my statement or bills?",
    para: `On your bank or credit card statement, you'll see your transactions as Epoch. This ensures complete confidentiality of your purchases.`,
  },
  {
    category: 'Tokens & Payments',
    heading: 'My bank declined the payment for my subscription; what should I do next?',
    para: `A card payment can be declined for several reasons. If your card is declined for a payment on CosplayFans you can reach out directly to us, or you can contact our official payment processor at Epoch.com.`,
  },
  // Subscriptions
  {
    category: 'Subscriptions',
    heading: 'How do I subscribe to a creator?',
    para: `You can go directly to the creator's personal page and there you will see a "Subscribe" button. After you click the subscribe button you will be prompted to enter your card details for a successful transaction. After that, enjoy your favorite creator's content!`,
  },
  {
    category: 'Subscriptions',
    heading: "I've subscribed to more than one cosplay creator, how will I be billed?",
    para: `Each subscription is a separate transaction. If you have paid for your subscription through your debit or credit card, it will be charged again at the beginning of your subscription period for each creator.`,
  },
  {
    category: 'Subscriptions',
    heading: "I've subscribed to a creator, what happens next?",
    para: `Enjoy your creator's content and private photos. At any time you control the aspect of your character from backgrounds, undressing, change clothes and more!`,
  },
  {
    category: 'Subscriptions',
    heading: 'How can I cancel a subscription to a creator?',
    para: `To cancel a subscription, sign into your account and access the Subscriptions tab. Here you will find all of your active subscriptions. Simply find the creator you would like to cancel and then click on the option next to their name and hit the Cancel Subscription option.`,
  },
  {
    category: 'Subscriptions',
    heading: 'How do Rewards work?',
    para: `Rewards run automatically on the tokens you spend. Every token you spend — whether you bought it or earned it — counts toward your lifetime total, which moves you up through 10 levels from Newcomer to Mythic. The moment you reach a new level we instantly credit bonus tokens to your balance. They never expire, and past Mythic you keep earning through endless Prestige levels. You can track your level and bonuses anytime on the Rewards page in My Account.`,
  },
  // Content & Creators
  {
    category: 'Content & Creators',
    heading: "What if I'm not happy with the content I've purchased?",
    para: `When subscribing to a creator you get exclusive free access to the creator's inbox. You can drop them a message and tell them what you want to see more of. Still not happy? You can cancel your subscription at any time.`,
  },
  {
    category: 'Content & Creators',
    heading: "How can I express my appreciation for a creator's work?",
    para: `When you love what a creator does, you can send them a tip, subscribe to their exclusive content, or let them know through a direct message!`,
  },
  // Support
  {
    category: 'Support',
    heading: 'How can I contact CosplayFans?',
    para: `If you still have questions for us, you can go to our <a href="/contact-us" class="text-[var(--cf-cyan)] hover:underline">Contact Us</a> page, or email <a href="mailto:support@cosplayfans.ai" class="text-[var(--cf-cyan)] hover:underline">support@cosplayfans.ai</a>.`,
  },
  // Privacy & Safety
  {
    category: 'Privacy & Safety',
    heading: 'Is my activity private and discreet?',
    para: `Your privacy is a top priority. All traffic is protected with industry-standard 256-bit SSL encryption, and your billing statement shows a neutral, discreet descriptor — never CosplayFans — so your purchases stay confidential. We never sell your personal data or share your activity, and you can delete your account and content at any time from your settings.`,
  },
]

const categories = [...new Set(faqData.map((item) => item.category))]

function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return function (...args: Parameters<T>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  } as T
}

export default function FAQ() {
  const [searchQuery, setSearchQuery] = createSignal('')
  const [searchResults, setSearchResults] = createSignal<FAQItem[]>(faqData)
  const [openItems, setOpenItems] = createSignal<Set<string>>(new Set())
  let fuse: Fuse<FAQItem>
  let inputRef: HTMLInputElement | undefined

  createEffect(() => {
    fuse = new Fuse(faqData, {
      keys: ['heading', 'para', 'category'],
      includeMatches: true,
      includeScore: true,
      minMatchCharLength: 2,
      threshold: 0.4,
    })
  })

  const handleSearch = debounce((value: string) => {
    setSearchQuery(value)
    if (!value.trim()) {
      setSearchResults(faqData)
      setOpenItems(new Set<string>())
      return
    }
    const results = fuse.search(value)
    if (results.length) {
      setSearchResults(results.map((r) => r.item))
      setOpenItems(new Set<string>(results.map((r) => r.item.heading)))
    } else {
      setSearchResults([])
      setOpenItems(new Set<string>())
    }
  }, 250)

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults(faqData)
    setOpenItems(new Set<string>())
    if (inputRef) inputRef.value = ''
  }

  const toggleItem = (heading: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev)
      if (next.has(heading)) {
        next.delete(heading)
      } else {
        next.add(heading)
      }
      return next
    })
  }

  const groupedResults = () => {
    const results = searchResults()
    const groups: { category: string; items: FAQItem[] }[] = []
    for (const cat of categories) {
      const items = results.filter((item) => item.category === cat)
      if (items.length > 0) {
        groups.push({ category: cat, items })
      }
    }
    return groups
  }

  return (
    <>
      <Title>CosplayFans - FAQ | Get Answers About Fan Pages</Title>
      <Meta name="og:title" content="CosplayFans - FAQ | Get Answers About Fan Pages" />
      <Meta
        name="description"
        content="Find answers to frequently asked questions about CosplayFans.ai features including exclusive content, live chat, ai generated images, subscriptions & support."
      />
      <Meta
        name="og:description"
        content="Find answers to frequently asked questions about CosplayFans.ai features including exclusive content, live chat, ai generated images, subscriptions & support."
      />
      <Link rel="canonical" href={getCanonicalUrl('/faq/')} />

      <main>
        {/* Hero */}
        <PageHero
          badge="Help Center"
          title={<>FREQUENTLY ASKED <span class="cf-grad-text">QUESTIONS</span></>}
          subtitle="Everything you need to know about CosplayFans — from tokens to chats to image generation."
        />

        <div class="cf-section mx-auto max-w-[820px]" style={{ 'padding-top': '0' }}>
          {/* Search bar */}
          <div class="mx-auto mb-8 max-w-[520px]">
            <div class="relative">
              {/* Search icon */}
              <div class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="rgba(255,255,255,0.35)"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search questions..."
                class="w-full rounded-xl border border-[rgba(255,255,255,0.07)] bg-[rgba(16,12,30,0.6)] py-3.5 pl-11 pr-10 font-nunito text-sm text-white outline-none transition-all duration-200 placeholder:text-[rgba(255,255,255,0.3)] focus:border-[rgba(138,43,226,0.5)] focus:shadow-[0_0_24px_rgba(138,43,226,0.15)]"
                onInput={(e) => handleSearch(e.currentTarget.value)}
              />
              {/* Clear button */}
              <Show when={searchQuery().length > 0}>
                <button
                  onClick={clearSearch}
                  class="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(255,255,255,0.08)] text-[rgba(255,255,255,0.5)] transition-colors hover:bg-[rgba(255,255,255,0.15)] hover:text-white"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </Show>
            </div>
          </div>

          {/* Result counter when searching */}
          <Show when={searchQuery().length > 0}>
            <p class="mb-6 text-center font-rajdhani text-xs font-semibold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.4)]">
              Showing {searchResults().length} of {faqData.length} questions
            </p>
          </Show>

          {/* Empty state */}
          <Show when={searchResults().length === 0}>
            <div class="flex flex-col items-center py-16">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="mb-4"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <p class="mb-1 font-bebas text-xl tracking-wide text-[rgba(255,255,255,0.4)]">
                No matches found
              </p>
              <p class="mb-5 text-sm text-[rgba(255,255,255,0.3)]">
                Try different keywords or browse all questions.
              </p>
              <button
                onClick={clearSearch}
                class="cf-btn ghost"
                style={{ height: '40px', 'font-size': '12px' }}
              >
                Clear search
              </button>
            </div>
          </Show>

          {/* FAQ groups */}
          <Show when={searchResults().length > 0}>
            <div class="space-y-8">
              <For each={groupedResults()}>
                {(group) => (
                  <section>
                    {/* Category header */}
                    <div class="mb-4 flex items-center gap-3">
                      <div
                        class="h-5 w-1 rounded-full"
                        style={{ background: 'var(--cf-grad-brand)' }}
                      />
                      <h2 class="font-bebas text-lg tracking-wide text-white sm:text-xl">
                        {group.category}
                      </h2>
                      <span class="flex h-7 min-w-[28px] items-center justify-center rounded-full border border-[rgba(138,43,226,0.4)] bg-[rgba(138,43,226,0.18)] px-2 font-rajdhani text-[12px] font-bold text-white">
                        {group.items.length}
                      </span>
                    </div>

                    {/* FAQ items */}
                    <div class="space-y-2">
                      <For each={group.items}>
                        {(item) => {
                          const isOpen = () => openItems().has(item.heading)
                          return (
                            <div
                              class="overflow-hidden rounded-xl border transition-colors duration-200"
                              classList={{
                                'border-[rgba(138,43,226,0.4)] bg-[rgba(138,43,226,0.05)]':
                                  isOpen(),
                                'border-[rgba(255,255,255,0.07)] bg-[rgba(255,255,255,0.03)]':
                                  !isOpen(),
                              }}
                            >
                              <button
                                onClick={() => toggleItem(item.heading)}
                                class="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-[rgba(255,255,255,0.02)]"
                              >
                                <span class="pr-4 font-rajdhani text-[14px] font-semibold text-white sm:text-[15px]">
                                  {item.heading}
                                </span>
                                <span
                                  class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200"
                                  classList={{
                                    'bg-[rgba(138,43,226,0.2)] text-[var(--cf-primary)]': isOpen(),
                                    'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.4)]':
                                      !isOpen(),
                                  }}
                                >
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="transition-transform duration-200"
                                    classList={{ 'rotate-45': isOpen() }}
                                  >
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                  </svg>
                                </span>
                              </button>
                              <div
                                class="grid transition-all duration-200"
                                style={{
                                  'grid-template-rows': isOpen() ? '1fr' : '0fr',
                                }}
                              >
                                <div class="overflow-hidden">
                                  <div class="border-t border-[rgba(255,255,255,0.06)] px-5 pb-5 pt-4">
                                    <div
                                      class="font-nunito text-sm leading-relaxed text-[var(--cf-muted)]"
                                      innerHTML={item.para}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        }}
                      </For>
                    </div>
                  </section>
                )}
              </For>
            </div>
          </Show>
        </div>
      </main>
      <Footer />
    </>
  )
}
